import React, { forwardRef, useEffect, useId, useRef, useState } from "react";
import Text, { textV4Variants } from "@src/lib/ui/Text";
import { Button } from "@src/lib/ui/Button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@src/lib/ui/Tooltip";
import ChevronLeftIcon from "@src/lib/icons/12px/chevron-left";
import ChevronRightIcon from "@src/lib/icons/12px/chevron-right";
import { cn } from "@src/lib/utils/cn";
import {
  type QuestionAnswers,
  type QuestionTexts,
  type QuestionsState,
  answerText,
  answersFromReply,
  composeAnswers,
  picksFileOption,
  toggleAnswer,
} from "./questionPrompts";

interface OpusAgentQuestionsCardProps {
  state: QuestionsState;
  // Sends the composed answer message. An empty message sends nothing: the
  // caller only moves focus to the composer.
  onContinue: (message: string, withFile: boolean) => void;
}

// The card frame, shared by the open card and its folded summary.
const CARD_CLASS =
  "flex w-full min-w-0 flex-col overflow-hidden rounded-xl bg-bg-secondary";
// A row's top divider, inset 12px from each side.
const ROW_DIVIDER =
  "relative before:absolute before:top-0 before:right-3 before:left-3 before:border-t before:border-border-primary";
const FOCUS_RING =
  "focus-visible:ring-3 focus-visible:ring-brand-border focus-visible:outline-none";
// The white card a row lifts onto: Card container surface + elevated
// (bg-bg-primary, shadow-default), inset 4px in the borderless rounded-xl
// tray, so 12 − 4 → rounded-lg (design.md › Radius, nested). Content sits
// above it.
const LIFT_CARD =
  "after:pointer-events-none after:absolute after:inset-x-1 after:inset-y-0 after:rounded-lg after:transition-[background-color,box-shadow] [&>*]:relative [&>*]:z-10";
const LIFTED = "after:bg-bg-primary after:shadow-default";
const SMALL = textV4Variants({ variant: "fine-print-description" });
const SMALL_MEDIUM = cn(SMALL, "font-medium-v4");

const letter = (index: number) => String.fromCharCode(65 + index);

const LetterBadge: React.FC<{ index: number; selected?: boolean }> = ({
  index,
  selected = false,
}) => (
  <span
    aria-hidden="true"
    className={cn(
      SMALL_MEDIUM,
      "grid size-5 shrink-0 place-items-center rounded border tabular-nums",
      selected
        ? "border-action-primary bg-action-primary text-action-primary-foreground"
        : "border-border-primary text-text-tertiary",
    )}
  >
    {letter(index)}
  </span>
);

const CardHeader: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <div className="flex min-h-7 items-center justify-between gap-2 px-3 pt-2 pb-1.5">
    <span className={cn(SMALL_MEDIUM, "text-text-secondary")}>Questions</span>
    {children}
  </div>
);

const PagerButton = forwardRef<
  HTMLButtonElement,
  {
    label: string;
    disabled: boolean;
    onClick: () => void;
    children: React.ReactNode;
  }
>(({ label, disabled, onClick, children }, ref) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        ref={ref}
        type="button"
        variant="ghost"
        size="icon-xs"
        aria-label={label}
        disabled={disabled}
        onClick={onClick}
        className="text-icon-tertiary hover:text-icon-primary"
      >
        {children}
      </Button>
    </TooltipTrigger>
    <TooltipContent>{label}</TooltipContent>
  </Tooltip>
));
PagerButton.displayName = "PagerButton";

// The agent's questions before it plans, one at a time: lettered options, a
// "Something else…" line for a typed answer, Skip, and Continue. The card is
// a shortcut, not a form: typing or speaking an answer in the composer works
// the same. Once a message follows (or the chat can't answer), the card folds
// to a muted read-only summary of what was asked and, when it reads back,
// answered.
export const OpusAgentQuestionsCard: React.FC<OpusAgentQuestionsCardProps> = ({
  state,
  onContinue,
}) => {
  const [answers, setAnswers] = useState<QuestionAnswers>({});
  const [texts, setTexts] = useState<QuestionTexts>({});
  const [index, setIndex] = useState(0);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const previousRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  // A pager button that became disabled at an end while focused hands focus
  // to the other one, instead of letting it fall to the body.
  const pendingFocus = useRef<React.RefObject<HTMLButtonElement | null> | null>(
    null,
  );
  const idPrefix = useId();

  useEffect(() => {
    pendingFocus.current?.current?.focus();
    pendingFocus.current = null;
  }, [index]);

  if (state.status !== "open") {
    const replied = answersFromReply(state.questions, state.reply);
    return (
      <div
        data-testid="opus-agent-questions-card"
        data-questions-status={state.status}
        className={cn(CARD_CLASS, "pb-1")}
      >
        <CardHeader />
        {state.questions.map((question) => {
          const answer = replied.get(question.id);
          return (
            <div
              key={question.id}
              className={cn(ROW_DIVIDER, "min-w-0 px-3 py-1.5")}
              data-testid="opus-agent-questions-summary-row"
            >
              <Text
                variant="fine-print-description"
                multiline
                className="text-pretty text-text-tertiary"
              >
                {question.text}
              </Text>
              {answer && (
                <Text
                  variant="helper-title"
                  multiline
                  className="text-text-secondary"
                  data-testid="opus-agent-questions-summary-answer"
                >
                  {answer}
                </Text>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  const { questions, actionable } = state;
  const count = questions.length;
  const current = Math.min(index, count - 1);
  const question = questions[current];
  const isLast = current === count - 1;
  const labelId = `${idPrefix}-${question.id}`;
  const hintId = `${labelId}-hint`;
  const picked = answers[question.id] ?? [];
  const typed = texts[question.id] ?? "";
  const canContinue =
    actionable && answerText(question, answers, texts) !== null;
  const multi = question.multiSelect;
  // Roving tabindex: the group is one Tab stop, on the (first) selected
  // option, else the first.
  const tabStop = Math.max(
    0,
    question.options.findIndex((_, optionIndex) =>
      picked.includes(optionIndex),
    ),
  );
  // Skip on the last question sends the other answers; with none it would
  // send nothing, so it waits for one.
  const canSkip =
    actionable &&
    (!isLast ||
      composeAnswers(
        questions,
        { ...answers, [question.id]: [] },
        { ...texts, [question.id]: "" },
      ) !== "");

  const finish = (finalAnswers: QuestionAnswers, finalTexts: QuestionTexts) =>
    onContinue(
      composeAnswers(questions, finalAnswers, finalTexts),
      picksFileOption(questions, finalAnswers),
    );

  const next = () => {
    if (isLast) finish(answers, texts);
    else setIndex(current + 1);
  };

  // Skip leaves this question unanswered and moves on; on the last one it
  // sends whatever was answered (nothing, if nothing was).
  const skip = () => {
    const nextAnswers = { ...answers, [question.id]: [] };
    const nextTexts = { ...texts, [question.id]: "" };
    setAnswers(nextAnswers);
    setTexts(nextTexts);
    if (isLast) finish(nextAnswers, nextTexts);
    else setIndex(current + 1);
  };

  // An explicit pick (click, Space, Enter) and a typed answer are
  // exclusive: each clears the other. Picking the selected radio keeps it
  // (native radio behaviour) but still clears the typed answer.
  const pick = (optionIndex: number) => {
    setAnswers((existing) => toggleAnswer(existing, question, optionIndex));
    setTexts((existing) => ({ ...existing, [question.id]: "" }));
  };
  // On a radiogroup the arrow keys select as they move (ARIA radio pattern),
  // except while a typed answer is the one: then they only move focus, so no
  // radio ever looks checked while the typed text is what Continue sends.
  const arrowSelect = (optionIndex: number) =>
    setAnswers((existing) => ({ ...existing, [question.id]: [optionIndex] }));
  const typeAnswer = (value: string) => {
    setTexts((existing) => ({ ...existing, [question.id]: value }));
    if (value.trim())
      setAnswers((existing) => ({ ...existing, [question.id]: [] }));
  };

  const page = (target: number) => {
    const active = document.activeElement;
    if (target === 0 && active === previousRef.current)
      pendingFocus.current = nextRef;
    else if (target === count - 1 && active === nextRef.current)
      pendingFocus.current = previousRef;
    setIndex(target);
  };

  // Up/Down move between the options; on a radiogroup they also select
  // (the ARIA radio pattern), on checkboxes they only move.
  const onOptionKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    optionIndex: number,
  ) => {
    const step =
      event.key === "ArrowDown" ? 1 : event.key === "ArrowUp" ? -1 : 0;
    if (!step) return;
    event.preventDefault();
    const total = question.options.length;
    const target = (optionIndex + step + total) % total;
    optionRefs.current[target]?.focus();
    if (!multi && !typed.trim()) arrowSelect(target);
  };

  return (
    <div
      data-testid="opus-agent-questions-card"
      data-questions-status={state.status}
      className={CARD_CLASS}
    >
      <CardHeader>
        {count > 1 && (
          <div className="flex shrink-0 items-center gap-0.5">
            <PagerButton
              ref={previousRef}
              label="Previous question"
              disabled={current === 0}
              onClick={() => page(current - 1)}
            >
              <ChevronLeftIcon aria-hidden="true" />
            </PagerButton>
            <span
              className={cn(SMALL, "px-0.5 tabular-nums text-text-tertiary")}
              data-testid="opus-agent-questions-position"
            >
              {current + 1} of {count}
            </span>
            <PagerButton
              ref={nextRef}
              label="Next question"
              disabled={isLast}
              onClick={() => page(current + 1)}
            >
              <ChevronRightIcon aria-hidden="true" />
            </PagerButton>
          </div>
        )}
      </CardHeader>
      <Text
        id={labelId}
        variant="compact-title"
        multiline
        className="px-3 pb-2 text-pretty text-text-primary"
        data-testid="opus-agent-question-text"
      >
        {question.text}
      </Text>
      <div
        role={multi ? "group" : "radiogroup"}
        aria-labelledby={labelId}
        aria-describedby={hintId}
        className="flex min-w-0 flex-col"
      >
        {question.options.map((option, optionIndex) => {
          const selected = picked.includes(optionIndex);
          return (
            <button
              // Index keys: option labels may repeat.
              key={`${question.id}-${optionIndex}`}
              ref={(node) => {
                optionRefs.current[optionIndex] = node;
              }}
              type="button"
              role={multi ? "checkbox" : "radio"}
              aria-checked={selected}
              tabIndex={optionIndex === tabStop ? 0 : -1}
              disabled={!actionable}
              onClick={() => pick(optionIndex)}
              onKeyDown={(event) => onOptionKeyDown(event, optionIndex)}
              data-testid="opus-agent-question-option"
              className={cn(
                ROW_DIVIDER,
                FOCUS_RING,
                "flex min-w-0 items-center gap-2 px-3 py-1.5 text-left transition-colors focus-visible:ring-inset disabled:opacity-50",
                // Hover and selection lift the row onto a white card, inset
                // 4px, with the dividers above and below it hidden; content
                // sits above the card.
                LIFT_CARD,
                "enabled:hover:after:bg-bg-primary enabled:hover:after:shadow-default enabled:hover:before:border-transparent enabled:[&:hover+*]:before:border-transparent",
                selected &&
                  cn(
                    LIFTED,
                    "before:border-transparent [&+*]:before:border-transparent",
                  ),
              )}
            >
              <LetterBadge index={optionIndex} selected={selected} />
              {/* Inline text (a div can't sit in a button): the label wraps
                  instead of clipping in a narrow panel. */}
              <span
                className={cn(
                  textV4Variants({ variant: "helper-title" }),
                  "min-w-0 break-words whitespace-normal",
                  selected ? "text-text-primary" : "text-text-secondary",
                )}
              >
                {option}
              </span>
            </button>
          );
        })}
      </div>
      <div
        className={cn(
          ROW_DIVIDER,
          LIFT_CARD,
          "flex min-w-0 items-center gap-2 px-3 py-1.5",
          // The last option's white card would otherwise sit on this divider.
          "[*:has(>:last-child:enabled:hover)+&]:before:border-transparent [*:has(>:last-child[aria-checked=true])+&]:before:border-transparent",
          // Focusing or filling the field lifts the row like a selected
          // option; the brand ring marks keyboard focus (the field's focus
          // ring, drawn on the borderless inline row).
          "focus-within:before:border-transparent has-[input:focus-visible]:ring-3 has-[input:focus-visible]:ring-brand-border has-[input:focus-visible]:ring-inset",
          typed.trim() !== "" && cn(LIFTED, "before:border-transparent"),
          "focus-within:after:bg-bg-primary focus-within:after:shadow-default",
        )}
      >
        <LetterBadge index={question.options.length} />
        <input
          // A fresh input per question.
          key={question.id}
          type="text"
          value={typed}
          onChange={(event) => typeAnswer(event.target.value)}
          onKeyDown={(event) => {
            // Enter that commits an IME composition isn't a submit.
            const composing =
              event.nativeEvent.isComposing || event.keyCode === 229;
            if (event.key === "Enter" && !composing && canContinue) {
              event.preventDefault();
              next();
            }
          }}
          disabled={!actionable}
          placeholder="Something else…"
          aria-label={`Something else: ${question.text}`}
          data-testid="opus-agent-question-other"
          // Borderless inline field (the owner's design); text, placeholder
          // and disabled follow the field look (design.md › Input).
          className={cn(
            textV4Variants({ variant: "helper-description" }),
            "h-5 min-w-0 flex-1 bg-transparent text-text-primary outline-none placeholder:text-text-tertiary disabled:cursor-not-allowed disabled:opacity-50",
          )}
        />
        <Button
          type="button"
          variant="ghost"
          size="xs"
          disabled={!canSkip}
          onClick={skip}
          data-testid="opus-agent-questions-skip"
          className="shrink-0 text-text-tertiary hover:text-text-secondary"
        >
          Skip
        </Button>
      </div>
      {/* Outside the Something else row (its [&>*]:relative would undo sr-only) and after it (its divider rule reads the options group as its previous sibling). */}
      <span id={hintId} className="sr-only">
        Or type another answer in Something else, below the options.
      </span>
      <div className="flex items-center p-2">
        <Button
          variant="outline"
          size="sm"
          className="w-full"
          disabled={!canContinue}
          onClick={next}
          data-testid="opus-agent-questions-continue"
        >
          Continue
        </Button>
      </div>
    </div>
  );
};
